import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { TodayEventsService } from './services/today-events.service';
import { HttpClient } from '@angular/common/http';
import { Subject, forkJoin, interval, combineLatest } from 'rxjs';
import { Events } from '../shared/models/event-models';
import { switchMap, takeUntil } from 'rxjs/operators';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';

@Component({
    selector: 'app-today-events',
    templateUrl: './today-events.component.html',
    styleUrls: ['./today-events.component.scss'],
    standalone: false
})
export class TodayEventsComponent implements OnInit, OnDestroy {
  events: Events[] = [];
  private destroy$: Subject<void> = new Subject<void>();
  faCircleNotch = faCircleNotch;
  public showSpinner: boolean = true;

  constructor(
    protected http: HttpClient,
    protected service: TodayEventsService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.loadEvents();

    interval(5 * 60 * 1000) /*every 5 min*/
      .pipe(
        takeUntil(this.destroy$),
        switchMap(async () => this.loadEvents())
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadEvents(): void {
   combineLatest([
     this.service.getAdminEvents(),
     this.service.getTodayHearings(),
     this.service.getTodaySpecialEvents(),
     this.service.getTodaySessions()
    ]).subscribe(
      ([adminEvents, hearings, specialEvents, sessions]) => {
        const allEvents = [
          ...this.transformToEvents(adminEvents),
          ...this.transformToEvents(hearings),
          ...this.transformToEvents(specialEvents),
          ...this.transformToEvents(sessions)
        ];
        const todayEvents = allEvents.filter(event => this.isEventToday(event.eventDate));

        this.events = todayEvents.sort((a, b) => {
          const timeA = new Date(`2000-01-01 ${a.startTime}`).getTime();
          const timeB = new Date(`2000-01-01 ${b.startTime}`).getTime();
          return timeA - timeB;
        });
        this.showSpinner = false;
        this.cdr.detectChanges();
      },
      error => {
        console.error('Error loading events: ', error);
        this.showSpinner = false;
        this.cdr.detectChanges();
      }
    );
  }

  private transformToEvents(data: any[]): any[] {
    return data.map(item => ({
      startTime: item.startTime ? this.extractTimeFromDateTime(item.startTime) : this.extractTimeFromDateTime(item.date),
      eventDate: item.startTime ? new Date(item.startTime).toDateString() : new Date(item.date).toDateString(),
      name: item.name ? item.name : item.eventTitle,
      location: item.location
    }))
    .sort((a, b) => {
      const timeA = new Date(`2000-01-01 ${a.startTime}`).getTime();
      const timeB = new Date(`2000-01-01 ${b.startTime}`).getTime();
      return timeA - timeB;
    });
  }

  private extractTimeFromDateTime(dateTimeString: string): string {
    const dateTime = new Date(dateTimeString);
    let hours = dateTime.getHours();
    const minutes = dateTime.getMinutes();
    const amPm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    const hoursString = hours.toString();
    const minutesString = minutes < 10 ? '0' + minutes : minutes.toString();
    return `${hoursString}:${minutesString} ${amPm}`;
  }

  private isEventToday(startTime: string): boolean {
    const today = new Date().toDateString(); 
    const eventDate = new Date(startTime).toDateString();
    return eventDate === today;
  }
}
